/**
 * color.ts — culori-backed OKLCH helpers for tenant-primary shade derivation.
 *
 * NAMED IMPORTS ONLY (RESEARCH.md Pitfall 3 — wildcard imports defeat tree-shaking;
 * verified ~6KB gzipped after treeshake). Each helper is a thin wrapper that
 * 1) parses the input hex, 2) interpolates in OKLCH space (perceptually uniform,
 * handles hue wraparound correctly), and 3) clamps chroma into sRGB gamut so
 * we never emit out-of-gamut colors that render inconsistently across displays.
 *
 * @see /tmp/design-refresh-2026-04-18/hr-system/project/src/app.jsx:19 — original
 *      `document.documentElement.style.setProperty('--primary', hex)` pattern;
 *      tenant-primary.ts builds the full 5-variable runtime setter on top of this.
 */
import { parse, formatHex, oklch, interpolate, clampChroma } from "culori";

export interface ParsedColor {
  /** OKLCH lightness ∈ [0, 1]. */
  l: number;
  /** OKLCH chroma. */
  c: number;
  /** OKLCH hue in degrees; undefined for achromatic inputs (pure gray/black/white). */
  h?: number;
}

/**
 * Parses a hex or CSS color string and returns OKLCH components.
 * Returns null if the input is invalid — callers MUST null-check before use.
 */
export function parseHex(hex: string): ParsedColor | null {
  const parsed = parse(hex);
  if (!parsed) return null;
  const o = oklch(parsed);
  if (!o) return null;
  const result: ParsedColor = { l: o.l ?? 0, c: o.c ?? 0 };
  if (o.h !== undefined) result.h = o.h;
  return result;
}

/**
 * Interpolates `hex` toward white in OKLCH space, then clamps chroma to the
 * sRGB gamut. `t=0` returns the input hex (within hex-serialization rounding);
 * `t=1` approaches pure white.
 *
 * Gamut-safety: highly-chromatic inputs near the sRGB boundary (e.g., neon pink,
 * electric blue) can produce interpolated points that live outside sRGB — we
 * clamp chroma inward so every output renders identically on sRGB displays.
 */
export function interpolateToWhite(hex: string, t: number): string {
  return interpolateToward(hex, "white", t);
}

/**
 * Interpolates `hex` toward an arbitrary `target` color in OKLCH space, then
 * clamps to the sRGB gamut. The generic form behind `interpolateToWhite` /
 * `interpolateToBlack`; used directly to tint a brand toward the DARK theme's
 * surface (dark mode's equivalent of "tint toward white").
 */
export function interpolateToward(hex: string, target: string, t: number): string {
  const interp = interpolate([hex, target], "oklch");
  const clamped = clampChroma(interp(t), "oklch");
  return formatHex(clamped) ?? hex;
}

/**
 * Interpolates `hex` toward black in OKLCH space, then clamps to sRGB gamut.
 * `t=0` returns the input hex; `t=1` approaches pure black.
 */
export function interpolateToBlack(hex: string, t: number): string {
  return interpolateToward(hex, "black", t);
}

/** Convenience — returns just the OKLCH lightness (0..1) of a hex, or null if invalid. */
export function getLightness(hex: string): number | null {
  const p = parseHex(hex);
  return p ? p.l : null;
}
